import { useEffect, useState, useRef } from "react";
import { FreeNumberInput } from "@/components/ui/free-number-input";
import { Gear, FloppyDisk, Copy, ArrowSquareOut, Plus, X, Lock, IdentificationCard, CurrencyDollar, Sparkle, CaretDown, CaretRight, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser } from "@/lib/api";
import { copyText } from "@/lib/io";
import InfoTip from "@/components/InfoTip";
// SiloSurfaceTunePanel moved to OperationsTab May 25 2026.
import StripeConnectInfoTip from "@/components/StripeConnectInfoTip";
import SiloRenameAudit from "@/components/SiloRenameAudit";
import PricingChangeModal from "@/components/PricingChangeModal";
import CollaboratorsPanel from "@/components/CollaboratorsPanel";
import SiloIconCard from "@/components/SiloIconCard";
import SiloAnywhereCard from "@/components/SiloAnywhereCard";
import LinkLibraryDrawer from "@/components/LinkLibraryDrawer";
import WidgetAppearanceCard from "@/components/WidgetAppearanceCard";
import WidgetRosterCard from "@/components/WidgetRosterCard";
import { SiloKindPill, SiloKindConvertModal, SILO_KIND_META, resolveSiloKind } from "@/components/SiloKindPill";
import { VOICE_OPTIONS } from "@/lib/useVoiceQA";

const TEMPLATES = [
  { id: "academic", label: "Academic" },
  { id: "tactical", label: "Tactical" },
  { id: "modern", label: "Modern" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SiloSettings({ silo, onUpdated }) {
  const [form, setForm] = useState({
    name: silo.name,
    tagline: silo.tagline || "",
    template: silo.template,
    // Feb 22, 2026 (Sprint Request 2) — first-touch attribution + its toggle.
    // Jun 2026 (LINKS_CONSOLE_SPRINT.md · §2) — the rotating 5-slot pool that
    // used to sit beside it is deleted platform-wide. Its editor was already
    // removed from this panel, but the state, the "add at least one" validator
    // and the payload key were all still here, so every Identity save silently
    // re-wrote "According to <name>," into the database from a UI nobody could
    // see. All of that is gone now.
    attribution_first_touch: silo.attribution_first_touch || "",
    attribution_first_touch_enabled: silo.attribution_first_touch_enabled !== false,
    allow_export: silo.allow_export,
    model_name: silo.model_name || "",
    max_output_tokens: silo.max_output_tokens ?? 2048,
    default_max_output_tokens: silo.default_max_output_tokens ?? 2048,
    access_tier: silo.access_tier || "public",
    subscriber_price_cents: silo.subscriber_price_cents || 0,
    premium_price_cents: silo.premium_price_cents || 0,
    // Sep 2026 — Reply-To on the fan welcome email. Blank = replies go to
    // My Silo support instead of the creator.
    fan_reply_to_email: silo.fan_reply_to_email || "",
    // iter-21 composer copy
    composer_placeholders: (silo.composer_placeholders && silo.composer_placeholders.length)
      ? silo.composer_placeholders
      : [`Ask ${silo.name} a question…`],
    // Tags (Feb 13, 2026) — short topic labels for tag discovery.
    tags: Array.isArray(silo.tags) ? silo.tags : [],
    // Silo Wars category (May 11, 2026) — single-bucket label powering
    // opponent search. Defaults to "other" when unset so the dropdown
    // always has a selected value.
    category: (silo.category || "other").toLowerCase(),
    // Jun 2026 — Voice conversation (Public Q&A).
    voice_enabled: !!silo.voice_enabled,
    voice_id: silo.voice_id || "onyx",
    voice_user_selectable: !!silo.voice_user_selectable,
    // Feb 2026 (Memory Sprint Phase 4 · Items 17+18) — Answer Quality
    // toggles. Both default OFF (null on the server side; false in the
    // form). Owner opts in per silo; each adds ONE extra LLM call per
    // ask on the SILO OWNER's BYOK provider key (the same key that
    // pays for the main /ask call).
    query_expansion_enabled: !!silo.query_expansion_enabled,
    answer_verification_enabled: !!silo.answer_verification_enabled,
    // Sep 30, 2026 (Randy pt-anti-ai) — Plain Human Voice defaults ON, so a
    // missing field is TRUE here. Only an explicit false switches it off.
    anti_ai_rules_enabled: silo.anti_ai_rules_enabled !== false,
    // Oct 5 2026 (Randy pt-conversation-depth) — Conversation-aware answers
    // also defaults ON, so a missing field is TRUE here.
    conversation_aware_enabled: silo.conversation_aware_enabled !== false,
    // Jun 2026 (Randy pt-relevant-links) — the default is computed on the
    // server (it depends on the silo's link profile), so trust the resolved
    // value it hands back rather than guessing here.
    relevant_links_enabled: silo.relevant_links_enabled ?? !!silo.relevant_links_effective,
    relevant_links_max: silo.relevant_links_max ?? 2,
    // Phase A3 — free pass defaults ON (null reads as on), model pass OFF.
    auto_fix_links: silo.auto_fix_links !== false,
    auto_fix_links_model: silo.auto_fix_links_model === true,
  });
  const [saving, setSaving] = useState(false);
  // Bump to force the Rename Audit to re-run (e.g. after a save or auto-fix) —
  // fixes the stale-audit bug where editing a flagged field never refreshed it.
  const [auditReload, setAuditReload] = useState(0);
  const [registry, setRegistry] = useState(null);
  const publicUrl = `${BACKEND_URL?.replace(/\/$/, "")}/s/${silo.slug}`;

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/models/registry");
        if (!cancel) setRegistry(r.data);
      } catch { /* silent — fall back to free-text input below */ }
    })();
    return () => { cancel = true; };
  }, []);

  // Composer placeholders — Fans see one at random on each visit.
  const setPlaceholder = (i, value) => {
    const next = [...form.composer_placeholders];
    next[i] = value;
    setForm({ ...form, composer_placeholders: next });
  };
  const addPlaceholder = () => {
    if (form.composer_placeholders.length >= 5) return;
    setForm({ ...form, composer_placeholders: [...form.composer_placeholders, ""] });
  };
  const removePlaceholder = (i) => {
    if (form.composer_placeholders.length <= 1) return;
    setForm({ ...form, composer_placeholders: form.composer_placeholders.filter((_, idx) => idx !== i) });
  };

  // Model-aware caps no longer needed at this level — SiloSurfaceTunePanel
  // owns model + tokens + memory + bridge per surface now. Keeping a stub
  // for the legacy public_ask cap clamp on the silo doc itself.
  const modelInfo = (() => {
    if (!registry?.providers) return { cap: 8192 };
    const m = form.model_name || registry.platform_default_model;
    for (const p of registry.providers) {
      if ((p.max_output_cap || {})[m]) return { cap: p.max_output_cap[m] };
    }
    return { cap: 8192 };
  })();

  // When the silo's legacy public_ask model changes, ensure max_output_tokens
  // doesn't exceed the new cap.
  useEffect(() => {
    if (form.max_output_tokens > modelInfo.cap) {
      setForm((f) => ({ ...f, max_output_tokens: modelInfo.cap }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelInfo.cap]);

  const save = async () => {
    const cleanedPlaceholders = form.composer_placeholders.map((s) => s.trim()).filter(Boolean);
    if (cleanedPlaceholders.length === 0) return toast.error("Add at least one chat placeholder.");
    setSaving(true);
    try {
      const payload = { ...form, composer_placeholders: cleanedPlaceholders };
      // Feb 22, 2026 (Sprint R2) — normalize first-touch text so a whitespace-
      // only value clears the field cleanly on save.
      payload.attribution_first_touch = (form.attribution_first_touch || "").trim();
      payload.attribution_first_touch_enabled = !!form.attribution_first_touch_enabled;
      // Don't send empty model_name (means "use platform default")
      if (!payload.model_name) delete payload.model_name;
      const r = await api.patch(`/silos/${silo.id}`, payload);
      if (r.data?.pending_price_change) {
        setPendingPriceChange(r.data.pending_price_change);
      } else {
        toast.success("Settings saved.");
      }
      onUpdated?.(r.data);
      setAuditReload((n) => n + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // iter-40 — Per-card save: Identity. Persists name, tagline, first-touch,
  // composer placeholders, UI template. Leaves pricing / LLM untouched.
  const saveIdentity = async () => {
    const cleanedPlaceholders = form.composer_placeholders.map((s) => s.trim()).filter(Boolean);
    if (cleanedPlaceholders.length === 0) return toast.error("Add at least one chat placeholder.");
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        tagline: form.tagline,
        template: form.template,
        // Feb 22, 2026 (Sprint R2) — persist the first-touch + its toggle.
        attribution_first_touch: (form.attribution_first_touch || "").trim(),
        attribution_first_touch_enabled: !!form.attribution_first_touch_enabled,
        composer_placeholders: cleanedPlaceholders,
        tags: form.tags || [],
        category: form.category || "other",
      };
      const r = await api.patch(`/silos/${silo.id}`, payload);
      toast.success("Identity saved.");
      onUpdated?.(r.data);
      setAuditReload((n) => n + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // Auto-fix a single Rename Audit finding (Randy): scroll to the field so the
  // creator SEES where the change lands, let the scroll settle, THEN swap the
  // old name for the new one in the live form + persist + re-audit.
  //
  // The `attribution_lead_ins` branch is gone with the feature — the backend
  // stopped emitting those findings in Oct 2026, so this was already dead.
  const applyAuditSuggestion = (f, finding) => {
    if (finding.field === "tagline") return { ...f, tagline: finding.suggestion };
    if (finding.field === "composer_placeholders") {
      const next = [...f.composer_placeholders];
      const idx = finding.index ?? 0;
      if (idx < next.length) next[idx] = finding.suggestion;
      return { ...f, composer_placeholders: next };
    }
    return f;
  };

  const auditFixingRef = useRef(false);
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const handleAuditFix = async (finding) => {
    if (!finding || finding.suggestion === undefined) return;
    if (auditFixingRef.current) return; // one guided fix at a time
    auditFixingRef.current = true;
    try {
      const inputTestid =
        finding.field === "tagline" ? "settings-tagline"
        : finding.field === "composer_placeholders" ? `settings-placeholder-${finding.index ?? 0}`
        : null;
      const headerTestid =
        finding.field === "tagline" ? "settings-tagline-label"
        : finding.field === "composer_placeholders" ? "settings-placeholders-label"
        : null;
      const input = inputTestid ? document.querySelector(`[data-testid="${inputTestid}"]`) : null;
      const header = headerTestid ? document.querySelector(`[data-testid="${headerTestid}"]`) : null;

      // 1) scroll DOWN to the section and let it settle (stop)
      const anchor = header || input;
      if (anchor) { try { anchor.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { anchor.scrollIntoView(); } }
      await sleep(650);

      // 2) glimmer the section header (~2s) so the creator SEES what changes
      if (header) header.classList.add("audit-glimmer");
      await sleep(2000);

      // 3) NOW change the name (apply suggestion + persist)
      const nextForm = applyAuditSuggestion(form, finding);
      setForm(nextForm);
      if (input) input.classList.add("audit-fix-flash");
      setSaving(true);
      try {
        const cleanedPlaceholders = nextForm.composer_placeholders.map((s) => s.trim()).filter(Boolean);
        const payload = {
          name: nextForm.name,
          tagline: nextForm.tagline,
          template: nextForm.template,
          composer_placeholders: cleanedPlaceholders.length ? cleanedPlaceholders : [nextForm.tagline || nextForm.name],
          tags: nextForm.tags || [],
          category: nextForm.category || "other",
        };
        const r = await api.patch(`/silos/${silo.id}`, payload);
        onUpdated?.(r.data);
        toast.success("Fixed & saved.");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Auto-fix failed");
      } finally {
        setSaving(false);
        if (header) header.classList.remove("audit-glimmer");
      }

      // 4) let the change register, then glide BACK UP to the audit card
      await sleep(500);
      if (input) input.classList.remove("audit-fix-flash");
      const auditCard = document.querySelector('[data-testid="silo-rename-audit"]');
      if (auditCard) { try { auditCard.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { auditCard.scrollIntoView(); } }
      await sleep(300);

      // 5) re-audit — the fixed finding drops off the list
      setAuditReload((n) => n + 1);
    } finally {
      auditFixingRef.current = false;
    }
  };

  const handleAuditDismiss = () => setAuditReload((n) => n + 1);

  // Feb 18, 2026 — pending price change confirmation popup.
  const [pendingPriceChange, setPendingPriceChange] = useState(null);

  // iter-40 — Per-card save: Monetization. Persists subscriber_price_cents
  // ONLY. Premium price + access tier live in Fan gating below and have their
  // own save.
  //
  // Feb 18, 2026: when the new price differs from the current AND there's at
  // least one active subscriber, the backend stamps `pending_price_change` on
  // the silo instead of applying immediately. We open PricingChangeModal so
  // the creator can confirm (demote subs + cancel Stripe) or back out.
  const saveMonetization = async () => {
    setSaving(true);
    try {
      const payload = {
        subscriber_price_cents: form.subscriber_price_cents,
        fan_reply_to_email: (form.fan_reply_to_email || "").trim() || null,
      };
      const r = await api.patch(`/silos/${silo.id}`, payload);
      if (r.data?.pending_price_change) {
        setPendingPriceChange(r.data.pending_price_change);
      } else {
        toast.success("Monetization saved.");
      }
      onUpdated?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // iter-40 — Per-card save: Fan gating. Persists access_tier + premium price.
  const saveFanGating = async () => {
    setSaving(true);
    try {
      const payload = {
        access_tier: form.access_tier,
        subscriber_price_cents: form.subscriber_price_cents,
        premium_price_cents: form.premium_price_cents,
      };
      const r = await api.patch(`/silos/${silo.id}`, payload);
      if (r.data?.pending_price_change) {
        setPendingPriceChange(r.data.pending_price_change);
      } else {
        toast.success("Fan gating saved.");
      }
      onUpdated?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // iter-40 — Per-card save: Permissions (export toggle).
  const savePermissions = async () => {
    setSaving(true);
    try {
      const payload = { allow_export: form.allow_export };
      const r = await api.patch(`/silos/${silo.id}`, payload);
      toast.success("Permissions saved.");
      onUpdated?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // Jun 2026 — Per-card save: Voice conversation (Public Q&A).
  const saveVoice = async () => {
    setSaving(true);
    try {
      const payload = {
        voice_enabled: form.voice_enabled,
        voice_id: form.voice_id,
        voice_user_selectable: form.voice_user_selectable,
      };
      const r = await api.patch(`/silos/${silo.id}`, payload);
      toast.success("Voice settings saved.");
      onUpdated?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // Feb 2026 (Memory Sprint Phase 4 · Items 17+18) — Per-card save for
  // Answer Quality. The two toggles are semantically independent (either
  // can be on/off) but share one Save button since they live in the same
  // "Answer Quality" section header per Randy's UI decision.
  const saveAnswerQuality = async () => {
    setSaving(true);
    try {
      const payload = {
        query_expansion_enabled: form.query_expansion_enabled,
        answer_verification_enabled: form.answer_verification_enabled,
        anti_ai_rules_enabled: form.anti_ai_rules_enabled,
        conversation_aware_enabled: form.conversation_aware_enabled,
        relevant_links_enabled: form.relevant_links_enabled,
        relevant_links_max: Number(form.relevant_links_max) || 2,
        auto_fix_links: form.auto_fix_links !== false,
        auto_fix_links_model: form.auto_fix_links_model === true,
      };
      const r = await api.patch(`/silos/${silo.id}`, payload);
      toast.success("Answer Quality settings saved.");
      onUpdated?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const copyUrl = async () => {
    const ok = await copyText(publicUrl);
    toast[ok ? "success" : "error"](ok ? "Public link copied." : "Could not copy. Try selecting the link and pressing ⌘C.");
  };

  return (
    <div data-testid="settings-tab" className="pb-24">
      <PricingChangeModal
        open={!!pendingPriceChange}
        siloId={silo.id}
        siloName={silo.name}
        pending={pendingPriceChange}
        onClose={() => setPendingPriceChange(null)}
        onApplied={() => {
          setPendingPriceChange(null);
          // Refetch silo so the now-applied price + cleared pending_price_change flow up
          api.get(`/silos/${silo.id}`).then((r) => onUpdated?.(r.data)).catch(() => {});
        }}
        onCancelled={() => {
          // Roll the form back to the pre-change price so the UI matches reality
          setForm((f) => ({ ...f, subscriber_price_cents: silo.subscriber_price_cents || 0 }));
          setPendingPriceChange(null);
        }}
      />
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <Gear size={20} weight="duotone" className="text-[#4A5D4E]" />
          <h2 className="font-serif-display text-3xl text-[#1C1B1A]">Silo settings</h2>
        </div>
        <button onClick={save} disabled={saving} data-testid="settings-save-top"
          className="px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm disabled:opacity-60 flex items-center gap-1.5">
          <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
      <p className="text-sm text-[#6B6A68] mb-8">Tune the public-facing identity, pricing, and permissions for this silo.</p>

      {/* Feb 2026 (Memory Sprint Phase 4 · Items 17+18) — Answer Quality.
          LOCKED placement per handoff: at the VERY TOP of the settings
          page (above every other card) because these two toggles are the
          most impactful accuracy levers a creator has, so they get top
          billing. Two switches, both default OFF, both add ONE extra
          LLM call per ask on the SILO OWNER'S BYOK provider key (not the
          creator's). Copy transparency: the note under each toggle
          explains the cost impact in plain English. */}
      <AnswerQualityCard
        form={form}
        setForm={setForm}
        saving={saving}
        onSave={saveAnswerQuality}
        silo={silo}
        onUpdated={onUpdated}
      />

      {/* Feb 19, 2026 (Randy) — Silo Type card. Canonical home for
          converting between Public / YouTube Twin / Personal / Organizer.
          Sits at the very top so it's the first setting a confused user
          reaches (previously only reachable via the tiny pill on the
          Creator Dashboard tile). */}
      <SiloTypeCard silo={silo} onUpdated={onUpdated} />

      {/* Feb 23, 2026 — Collaboration. Lives at the top so an owner can
          easily share a silo and a collaborator instantly sees who else
          is on the team. */}
      <CollaboratorsPanel silo={silo} onSiloUpdated={onUpdated} />

      {/* Oct 2026 (Randy, Widget Roster Stage 3) — "a special section in that
          person's silo settings… it's a different section, because of course
          their silo could also create a widget for their own website." Renders
          nothing until this silo has actually been accepted onto someone
          else's widget. */}
      <WidgetAppearanceCard silo={silo} />

      {/* Oct 2026 (Randy, Widget Roster Stage 4) — the host's side: the people
          on THIS silo's widget. Sits next to its sibling above on purpose —
          "who's on my widget" beside "how I appear on theirs". Renders nothing
          for anyone without the Silo Anywhere SKU. */}
      <WidgetRosterCard silo={silo} />

      {/* Feb 23, 2026 — Per-silo home-screen icon. Used by Add-to-Home-
          Screen on iOS and PWA installs on Android. */}
      <SiloIconCard
        silo={silo}
        onSaved={() => api.get(`/silos/${silo.id}`).then((r) => onUpdated?.(r.data)).catch(() => {})}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border hairline rounded-lg p-6" data-testid="settings-identity-card">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <h3 className="font-serif-display text-xl flex items-center gap-2">
                <IdentificationCard size={18} weight="duotone" className="text-[#4A5D4E]" /> Identity
              </h3>
              <button
                type="button"
                onClick={saveIdentity}
                disabled={saving}
                data-testid="settings-save-identity"
                className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5"
              >
                <FloppyDisk size={12} weight="duotone" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
            <label className="block text-sm text-[#6B6A68] mb-1.5">Silo name</label>
            <input data-testid="settings-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />

            {/* iter-40e — Rename audit panel. Surfaces slug mismatch +
                user-authored fields that still reference the old name. Lets
                creator refresh the slug (old URL keeps redirecting). */}
            <SiloRenameAudit silo={silo} formName={form.name} onUpdated={onUpdated} reloadSignal={auditReload} onFix={handleAuditFix} onDismiss={handleAuditDismiss} />

            <label data-testid="settings-tagline-label" className="block text-sm text-[#6B6A68] mt-4 mb-1.5">Tagline</label>
            <input data-testid="settings-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />

            {/* Silo Wars category (May 11, 2026) — single-bucket label.
                Required. Powers the "Find opponents by category" search
                in the Battles Hub. Different from tags (which are free-form
                discovery labels). */}
            <div className="mt-4" data-testid="settings-category-section">
              <label className="block text-sm text-[#6B6A68] mb-1.5">
                Category <span className="text-[10px] text-[#C16E5A]">(required for opponent search)</span>
              </label>
              <p className="text-xs text-[#6B6A68] mb-2">
                Pick the single bucket that best describes this silo. Used by Silo Wars to surface you to creators looking for opponents in your space.
              </p>
              <select
                data-testid="settings-category-select"
                value={form.category || "other"}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full bg-white border hairline rounded-md px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm"
              >
                <option value="philosophy">Philosophy &amp; Theory</option>
                <option value="business">Business &amp; Strategy</option>
                <option value="creative">Creative &amp; Arts</option>
                <option value="wellness">Health &amp; Wellness</option>
                <option value="spirituality">Spirituality &amp; Religion</option>
                <option value="tech">Tech &amp; Engineering</option>
                <option value="education">Education &amp; Teaching</option>
                <option value="science">Science &amp; Research</option>
                <option value="politics">Politics &amp; Society</option>
                <option value="other">Other / Uncategorized</option>
              </select>
            </div>

            {/* Tags (Feb 13, 2026) — short topic labels for tag discovery.
                Up to 5, lowercase, hyphenated. Auto-normalized server-side. */}
            <div className="mt-4" data-testid="settings-tags-section">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm text-[#6B6A68]">Tags <span className="text-[10px] text-[#A89360]">(up to 50)</span></label>
                <span className="text-xs text-[#6B6A68]">{(form.tags || []).length}/50</span>
              </div>
              <p className="text-xs text-[#6B6A68] mb-2">Short topic labels — fans can browse silos by tag. e.g., <em>philosophy</em>, <em>theology</em>, <em>tactics</em>.</p>
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                {(form.tags || []).map((t, i) => (
                  <span
                    key={`${t}-${i}`}
                    data-testid={`settings-tag-pill-${i}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#4A5D4E]/10 text-[#4A5D4E] text-xs"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, tags: form.tags.filter((_, idx) => idx !== i) })}
                      data-testid={`settings-tag-remove-${i}`}
                      className="hover:text-[#C16E5A]"
                      aria-label={`Remove ${t}`}
                    >
                      <X size={10} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
              {(form.tags || []).length < 50 && (
                <input
                  data-testid="settings-tag-input"
                  placeholder="Type a tag and press Enter…"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      const raw = e.target.value.trim();
                      if (!raw) return;
                      const norm = raw.toLowerCase().replace(/[^a-z0-9\- ]/g, "").trim().replace(/\s+/g, "-").slice(0, 24);
                      if (!norm) return;
                      if ((form.tags || []).includes(norm)) {
                        e.target.value = "";
                        return;
                      }
                      setForm({ ...form, tags: [...(form.tags || []), norm] });
                      e.target.value = "";
                    }
                  }}
                  className="w-full bg-white border hairline rounded-md px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm"
                />
              )}
            </div>

            {/* Oct 6 2026 (Randy pt-attribution-leadin-eval) — the 5-slot
                rotating attribution pool was RETIRED, and stays retired:
                nobody used the rotation, and a prefix that CHANGES per ask
                sits ahead of the Tier 1 anchor where it destroys a 97%
                implicit-cache hit (~+1.5¢ per ask, invisibly).

                Jun 2026 (PIPELINE_SIMPLIFICATION_PROJECT.md §2.6) — Randy
                brought back the part that was worth keeping: "the lead in,
                should only be on the first response of a dialogue. If the
                person is going back and forth, in a conversation, it
                shouldnt be appearing in the responses." ONE fixed phrase, no
                rotation, first answer only. Fixed means cacheable, so this
                costs nothing the retired version cost. */}
            <div className="mt-5" data-testid="settings-first-touch">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  data-testid="settings-first-touch-enabled"
                  type="checkbox"
                  checked={!!form.attribution_first_touch_enabled}
                  onChange={(e) => setForm({ ...form, attribution_first_touch_enabled: e.target.checked })}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#1C1B1A] font-medium flex items-center gap-1.5">
                    Opening line on the first answer
                    <InfoTip width={360} side="right" testid="info-first-touch" label="First-answer opening line help">
                      One fixed phrase, used only on the very first answer someone gets. Follow-up answers in the same conversation never carry it.{"\n\n"}
                      On YouTube it opens your reply to a comment, but never a reply to your own reply — nobody greets the same person twice in one thread.{"\n\n"}
                      Leave it empty and your AI opens naturally, which is what the Voice Kit and Tone Architect are for. There is no rotation: one phrase, every time, so the prompt stays cacheable and the opening never feels random.
                    </InfoTip>
                  </div>
                  <p className="text-xs text-[#6B6A68] leading-relaxed mt-0.5">
                    A greeting on the first answer only — not on the back-and-forth that follows,
                    and never on a reply to your own reply.
                  </p>
                </div>
              </label>
              <input
                data-testid="settings-first-touch-input"
                value={form.attribution_first_touch}
                disabled={!form.attribution_first_touch_enabled}
                maxLength={120}
                onChange={(e) => setForm({ ...form, attribution_first_touch: e.target.value })}
                placeholder="Leave empty to open naturally — e.g. “Good question.”"
                className="mt-2 w-full px-3 py-2 border hairline rounded-md text-sm bg-white disabled:bg-[#F7F7F4] disabled:text-[#9B9992]"
              />
              <p className="mt-1 text-[11px] text-[#6B6A68] leading-relaxed">
                {form.attribution_first_touch_enabled && (form.attribution_first_touch || "").trim()
                  ? <>First answers will begin “{(form.attribution_first_touch || "").trim()}”. Everything after opens straight into the answer.</>
                  : <>No fixed opening. Your Voice Kit and Tone Architect decide how each answer starts.</>}
              </p>
              <p className="mt-1.5 text-[11px] text-[#6B6A68] leading-relaxed">
                If your answers already open with a greeting you did not put here, it is coming
                from a style specimen in your Voice Kit — the AI copies how those samples start.
              </p>
            </div>

            {/* iter-21 — Composer placeholders. Same UX as lead-ins. Fans see one
                of these at random in the public chat textarea so returning visitors
                get visual variety. */}
            <div className="mt-5">
              <div className="flex items-center justify-between mb-1.5">
                <label data-testid="settings-placeholders-label" className="text-sm text-[#6B6A68] flex items-center gap-1.5">
                  Public chat placeholder (up to 5)
                  <InfoTip width={340} side="right" testid="info-composer-placeholders" label="Composer placeholder help">
                    The faded hint text fans see inside the public chat input box before they start typing.{"\n\n"}
                    Add up to 5 variations — your silo picks one at random on each fan visit so returning users see fresh copy.{"\n\n"}
                    Tip: keep them short (≤80 chars) and inviting. e.g. "What would you ask {silo.name}?" or "Pose your hardest question…".
                  </InfoTip>
                </label>
                <span className="text-xs text-[#6B6A68]">{form.composer_placeholders.length}/5</span>
              </div>
              <div className="space-y-2">
                {form.composer_placeholders.map((p, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`placeholder-row-${i}`}>
                    <input
                      data-testid={`settings-placeholder-${i}`}
                      value={p}
                      onChange={(e) => setPlaceholder(i, e.target.value)}
                      placeholder={i === 0 ? "Required — primary placeholder" : "Optional variation…"}
                      className="flex-1 bg-white border hairline rounded-md px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                    />
                    <button
                      type="button"
                      onClick={() => removePlaceholder(i)}
                      disabled={form.composer_placeholders.length <= 1}
                      data-testid={`settings-placeholder-remove-${i}`}
                      title="Remove"
                      className="p-2 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#F0F1EE] disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addPlaceholder}
                disabled={form.composer_placeholders.length >= 5}
                data-testid="settings-placeholder-add"
                className="mt-2 text-xs px-3 py-1.5 rounded-md border hairline bg-white text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={12} /> Add variation
              </button>
            </div>

            <label className="block text-sm text-[#6B6A68] mt-4 mb-1.5">UI template</label>
            <div className="grid grid-cols-3 gap-2">
              {TEMPLATES.map((t) => (
                <button key={t.id} type="button" onClick={() => setForm({ ...form, template: t.id })} data-testid={`settings-template-${t.id}`}
                  className={`text-sm px-3 py-2 rounded-md border ${form.template === t.id ? "border-[#4A5D4E] bg-[#F0F1EE] text-[#1C1B1A]" : "hairline text-[#6B6A68] hover:border-[#4A5D4E]/50"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border hairline rounded-lg p-6" data-testid="settings-monetization-card">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
              <h3 className="font-serif-display text-xl flex items-center gap-2">
                <CurrencyDollar size={18} weight="duotone" className="text-[#4A5D4E]" /> Monetization
                <StripeConnectInfoTip
                  exampleCents={form.subscriber_price_cents}
                  placement="bottom-right"
                  testid="silo-monetization-stripe-tip"
                  variant="monetization"
                  label="How does payment work?"
                />
              </h3>
              <button
                type="button"
                onClick={saveMonetization}
                disabled={saving}
                data-testid="settings-save-monetization"
                className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5"
              >
                <FloppyDisk size={12} weight="duotone" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
            <p className="text-sm text-[#6B6A68] mb-4">
              Set the monthly subscription price for fans. Hover the eye above for the full Stripe walkthrough — including a live status pill and a one-click "Connect Stripe" if you haven't onboarded yet.
            </p>
            <label className="block text-sm text-[#6B6A68] mb-1.5">Subscriber price (USD / month)</label>
            <div className="flex items-center gap-3">
              <span className="text-[#6B6A68]">$</span>
              <FreeNumberInput data-testid="settings-price" min={0} step={1} precision={2} value={form.subscriber_price_cents / 100}
                onChange={(v) => setForm({ ...form, subscriber_price_cents: Math.round(v * 100) })}
                className="w-40 bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
              <span className="text-xs text-[#6B6A68]">Set 0 for free public access.</span>
            </div>
            <p className="mt-2 text-xs text-[#6B6A68]" data-testid="settings-price-fee-note">
              This is what you receive. Supporters pay a small My Silo service fee on top, so your
              full price reaches you.
            </p>
            <StripeConnectHint wantsPaid={(form.subscriber_price_cents || 0) > 0} />

            <div className="mt-6 pt-5 border-t hairline">
              <label className="block text-sm text-[#6B6A68] mb-1.5">
                Where fan replies land <span className="text-xs">(optional)</span>
              </label>
              <input
                type="email"
                data-testid="settings-fan-reply-to"
                value={form.fan_reply_to_email}
                onChange={(e) => setForm({ ...form, fan_reply_to_email: e.target.value })}
                placeholder="you@yourdomain.com"
                className="w-full max-w-md bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              />
              <p className="text-xs text-[#6B6A68] mt-2 leading-relaxed">
                New subscribers get a welcome email in your voice — sent from My Silo's domain, so
                there's nothing for you to set up. Put an address here and their replies come
                straight to you. Leave it blank and replies go to My Silo support.
              </p>
            </div>
          </div>

          <div className="bg-white border hairline rounded-lg p-6" data-testid="settings-gating-card">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
              <h3 className="font-serif-display text-xl flex items-center gap-2">
                <Lock size={18} weight="duotone" className="text-[#4A5D4E]" /> Fan gating
              </h3>
              <button
                type="button"
                onClick={saveFanGating}
                disabled={saving}
                data-testid="settings-save-gating"
                className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5"
              >
                <FloppyDisk size={12} weight="duotone" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
            <p className="text-xs text-[#6B6A68] mb-4">
              Who can chat with this twin? Pricing is set above in <strong>Monetization</strong>; here you choose who that pricing applies to.
            </p>
            <label className="block text-sm text-[#6B6A68] mb-1.5">Access tier</label>
            <select data-testid="settings-access-tier" value={form.access_tier}
              onChange={(e) => setForm({ ...form, access_tier: e.target.value })}
              className="w-full bg-white border hairline rounded-md px-4 py-3 mb-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
              <option value="public">Public — anyone with the link can ask</option>
              <option value="subscriber">Subscriber — paid fans only (flat monthly)</option>
              <option value="premium">Premium — highest-tier subscribers only</option>
            </select>

            {form.access_tier === "premium" && (
              <div>
                <label className="block text-sm text-[#6B6A68] mb-1.5">Premium price <span className="text-[10px] uppercase tracking-[0.15em]">$/month</span></label>
                <FreeNumberInput min={0} max={1000} step={1}
                  data-testid="settings-premium-price"
                  value={Math.round((form.premium_price_cents || 0) / 100)}
                  onChange={(v) => setForm({ ...form, premium_price_cents: v * 100 })}
                  className="w-full bg-white border hairline rounded-md px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm" />
              </div>
            )}
          </div>

          {/* iter-40 — Per-surface LLM tune for THIS silo was here pre-
              May 25 2026. Moved into the Operations tab so all LLM
              controls (header card + per-surface tune) live in one
              place. Imports & component still exist; only the render
              location changed. */}

          {/* Silo Wars (add-on) — opt-in/out toggle for being challenged. */}
          <BattleAcceptsCard silo={silo} onUpdated={onUpdated} />

          <div className="bg-white border hairline rounded-lg p-6" data-testid="settings-permissions-card">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <h3 className="font-serif-display text-xl">Permissions</h3>
              <button
                type="button"
                onClick={savePermissions}
                disabled={saving}
                data-testid="settings-save-permissions"
                className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5"
              >
                <FloppyDisk size={12} weight="duotone" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input data-testid="settings-allow-export" type="checkbox" checked={form.allow_export}
                onChange={(e) => setForm({ ...form, allow_export: e.target.checked })} className="mt-1" />
              <div>
                <div className="text-sm font-medium text-[#1C1B1A]">Allow visitors to export conversations</div>
                <div className="text-xs text-[#6B6A68] mt-0.5">When off, the public chat hides the Export & Share dialog.</div>
              </div>
            </label>
          </div>

          {/* Jun 2026 — Voice conversation card. Turn voice on per silo, pick
              the spoken voice, and optionally let visitors switch it. */}
          <div className="bg-white border hairline rounded-lg p-6" data-testid="settings-voice-card">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <h3 className="font-serif-display text-xl">Voice conversation</h3>
              <button
                type="button"
                onClick={saveVoice}
                disabled={saving}
                data-testid="settings-save-voice"
                className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5"
              >
                <FloppyDisk size={12} weight="duotone" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input data-testid="settings-voice-enabled" type="checkbox" checked={form.voice_enabled}
                onChange={(e) => setForm({ ...form, voice_enabled: e.target.checked })} className="mt-1" />
              <div>
                <div className="text-sm font-medium text-[#1C1B1A]">Let visitors talk to this silo</div>
                <div className="text-xs text-[#6B6A68] mt-0.5">Adds a tiny mic to the chat box. Visitors speak, your silo answers from your content, and it reads the answer back aloud. You pay for the voice, same as answers.</div>
              </div>
            </label>
            {form.voice_enabled && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1C1B1A] mb-1">Voice</label>
                  <select
                    data-testid="settings-voice-id"
                    value={form.voice_id}
                    onChange={(e) => setForm({ ...form, voice_id: e.target.value })}
                    className="w-full text-sm rounded-md border hairline bg-white px-3 py-2 text-[#1C1B1A] focus:outline-none focus:ring-1"
                  >
                    {VOICE_OPTIONS.map((v) => (
                      <option key={v.id} value={v.id}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input data-testid="settings-voice-user-selectable" type="checkbox" checked={form.voice_user_selectable}
                    onChange={(e) => setForm({ ...form, voice_user_selectable: e.target.checked })} className="mt-1" />
                  <div>
                    <div className="text-sm font-medium text-[#1C1B1A]">Let visitors pick their own voice</div>
                    <div className="text-xs text-[#6B6A68] mt-0.5">Shows a voice dropdown in the chat's left-side Tools so visitors can switch the voice themselves.</div>
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>

        <aside className="space-y-6">
          <div className="bg-white border hairline rounded-lg p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]">Your blind link</div>
            <div className="mt-2 text-sm text-[#1C1B1A] font-mono break-all bg-[#F0F1EE] rounded px-3 py-2">{publicUrl}</div>
            <div className="mt-3 flex gap-2">
              <button onClick={copyUrl} data-testid="settings-copy-url"
                className="text-xs px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5">
                <Copy size={12} /> Copy
              </button>
              <a href={publicUrl}
                className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5">
                <ArrowSquareOut size={12} /> Open
              </a>
            </div>
            <p className="text-xs text-[#6B6A68] mt-3">No marketplace, no directory. Share this only with people you trust.</p>
          </div>

          {/* Oct 1, 2026 (Randy) — Silo Anywhere. Sits directly under the blind
              link, which is where he drew it. Saves on change, so it is
              deliberately outside this page's Save bar. */}
          <SiloAnywhereCard silo={silo} />
        </aside>
      </div>

      {/* Sticky save bar — always reachable even with long lead-in lists */}
      <div className="fixed bottom-0 left-0 right-0 z-20 bg-white/95 border-t hairline backdrop-blur-sm" data-testid="settings-save-bar">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-end gap-3 cta-safe">
          <span className="text-xs text-[#6B6A68] hidden sm:inline">Changes aren't saved until you click Save.</span>
          <button onClick={save} disabled={saving} data-testid="settings-save"
            className="px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm disabled:opacity-60 flex items-center gap-1.5">
            <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Lazy Stripe Connect onboarding hint — renders inside Fan gating card only when
// the creator is trying to charge fans but hasn't completed Express onboarding.
// Polls /stripe/config once on mount; avoids a round-trip when pricing is $0.
function StripeConnectHint({ wantsPaid }) {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    if (!wantsPaid) return;
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/stripe/config");
        if (!cancel) setCfg(r.data);
      } catch { /* silent */ }
    })();
    return () => { cancel = true; };
  }, [wantsPaid]);

  if (!wantsPaid || !cfg) return null;
  if (cfg.charges_enabled) return (
    <div className="mt-3 text-xs text-[#2F6B3A] flex items-center gap-1.5" data-testid="stripe-connect-hint-ready">
      <Sparkle size={12} weight="duotone" /> Stripe is ready — fans will be billed at your configured price.
    </div>
  );
  return (
    <div className="mt-3 p-3 rounded-md border-l-4 border-[#C16E5A] bg-[#FDF1ED]" data-testid="stripe-connect-hint-needed">
      <div className="text-sm text-[#1C1B1A] font-medium">Stripe setup required</div>
      <p className="text-xs text-[#6B6A68] mt-1">
        To charge fans, finish Stripe Express onboarding — you'll receive payouts directly.
      </p>
      <a href="/dashboard/account-settings" data-testid="stripe-connect-hint-link"
        className="inline-flex items-center gap-1.5 mt-2 text-xs px-3 py-1.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E]">
        Set up Stripe →
      </a>
    </div>
  );
}


// VisitorLimitsCard REMOVED Jun 2026 — clean-slate teardown of the visitor
// gating layer. The anonymous question budget it configured no longer exists.

// BattleAcceptsCard — Silo Wars opt-in/out toggle. Add-on: removing this
// component (and the call site above) cleanly removes the UI without
// touching the rest of settings. The `accepts_battles` field defaults to
// `null/missing` server-side, which is treated as "accepting" — silos
// already in the wild keep working until their owner explicitly opts out.
function BattleAcceptsCard({ silo, onUpdated }) {
  const [accepts, setAccepts] = useState(silo?.accepts_battles !== false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setAccepts(silo?.accepts_battles !== false); }, [silo?.id, silo?.accepts_battles]);

  const toggle = async () => {
    const next = !accepts;
    setAccepts(next);
    setSaving(true);
    try {
      await api.patch(`/battles/silos/${silo.id}/accepts`, { accepts_battles: next });
      toast.success(next ? "This silo can be challenged." : "This silo will refuse new challenges.");
      onUpdated && onUpdated();
    } catch (e) {
      setAccepts(!next); // revert
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border hairline rounded-lg p-6" data-testid="settings-battles-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-serif-display text-xl">Silo Wars</h3>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#A89360]">Add-on</span>
      </div>
      <div className="flex items-start gap-4">
        <button
          type="button"
          onClick={toggle}
          disabled={saving}
          aria-pressed={accepts}
          data-testid="settings-accepts-battles-toggle"
          className={`relative w-11 h-6 rounded-full transition flex-shrink-0 mt-1 ${accepts ? "bg-[#C16E5A]" : "bg-[#D8D6D2]"} ${saving ? "opacity-50" : ""}`}
        >
          <span className={`absolute top-0.5 ${accepts ? "left-5.5" : "left-0.5"} w-5 h-5 bg-white rounded-full shadow transition-all`} style={{ left: accepts ? "1.375rem" : "0.125rem" }} />
        </button>
        <div className="flex-1">
          <div className="text-sm font-medium text-[#1C1B1A] mb-1">
            {accepts ? "Accepting battle challenges" : "Refusing new battle challenges"}
          </div>
          <p className="text-[11px] text-[#6B6A68] leading-relaxed">
            {accepts
              ? "This silo shows up in the opponent dropdown when other creators issue challenges. You still review and accept (or decline) every incoming match before it runs."
              : "This silo is hidden from the opponent dropdown. New challenges to this silo will be blocked. Existing scheduled or pending battles aren't affected."}
          </p>
        </div>
      </div>
    </div>
  );
}


// Feb 2026 (Memory Sprint Phase 4 · Items 17+18) — Answer Quality card.
// Two per-silo opt-in toggles that each add ONE extra Gemini Flash call
// per ask on the SILO OWNER'S BYOK provider key (the same key that
// pays for the main /ask call — the owner covers the cost of every
// question their silo answers, including these accuracy passes).
// LOCKED placement per handoff: at the TOP of Silo Settings, above every
// other card, because these are the most impactful accuracy levers a
// creator has.
//
// UI pattern deliberately copies the voice-toggle checkbox-with-dependent-
// panel block (Randy's platform history has documented misfires from
// inventing new UI here, so we reuse the shape).
function AnswerQualityCard({ form, setForm, saving, onSave, silo, onUpdated }) {
  // Jun 2026 (Randy) — this family of toggles got long enough to need a
  // chevron. Open on first visit so nothing is hidden from a creator who has
  // never been here; remembers whatever state it was left in after that.
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("answerQualityCollapsed") !== "1"; } catch { return true; }
  });
  const toggleOpen = () => {
    setOpen((v) => {
      try { localStorage.setItem("answerQualityCollapsed", v ? "1" : "0"); } catch { /* private mode */ }
      return !v;
    });
  };
  // Oct 2026 (Randy) — the slider saves itself the moment you let go. It sits
  // near the bottom of a long card whose Save button is up at the top, which
  // is how a move from 2 to 4 reached production's UI and never reached its
  // database. One field, one PATCH, and the toast names the stored number so
  // there is no doubt it landed.
  const [savingMax, setSavingMax] = useState(false);
  const lastSavedMax = useRef(Number(form.relevant_links_max) || 2);
  const commitMax = async (n) => {
    if (!n || n === lastSavedMax.current || !form.relevant_links_enabled) return;
    lastSavedMax.current = n;
    setSavingMax(true);
    try {
      const r = await api.patch(`/silos/${silo.id}`, { relevant_links_max: n });
      onUpdated?.(r.data);
      toast.success(`Saved — up to ${n} link${n === 1 ? "" : "s"} in the closing offer.`);
    } catch {
      toast.error("Couldn't save the link count. Try the card's Save button.");
    } finally {
      setSavingMax(false);
    }
  };
  return (
    <section
      data-testid="silo-settings-answer-quality-card"
      className="bg-[#FDFCF8] border hairline rounded-lg p-6 mb-6"
    >
      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={toggleOpen}
              data-testid="answer-quality-chevron"
              aria-expanded={open}
              className="flex items-center gap-2 text-left hover:opacity-70 transition-opacity"
            >
              {open
                ? <CaretDown size={16} weight="bold" className="text-[#6B6A68] shrink-0" />
                : <CaretRight size={16} weight="bold" className="text-[#6B6A68] shrink-0" />}
              <h3 className="font-serif-display text-lg text-[#1C1B1A]">Answer Quality</h3>
            </button>
            <InfoTip
              width={340}
              side="right"
              testid="info-answer-quality"
              label="Answer Quality"
              text="Two optional accuracy boosts. Both add cost to YOUR silo's BYOK provider key (not the visitor's) — you pay for the extra LLM calls that power these features. Turn them on for specialized silos where visitors ask casual questions but your source material uses precise terms."
            />
          </div>
          <p className="text-xs text-[#6B6A68] leading-relaxed max-w-2xl mt-1">
            Two optional accuracy boosts that add cost to YOUR BYOK provider key — you pay for the extra LLM calls, not the visitor — plus two free rule sets: one that makes long conversations go deeper instead of repeating, and one that keeps answers from reading like AI.
          </p>
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid="answer-quality-save-btn"
          className="text-xs px-4 py-2 rounded-md border-2 border-[#1C1B1A] bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] hover:border-[#3A4A3E] disabled:opacity-60 flex items-center gap-1.5 shrink-0"
        >
          <FloppyDisk size={12} weight="duotone" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {/* Toggle 0 — Conversation-aware answers (Oct 5, 2026, Randy
          pt-conversation-depth). FREE, default ON. Listed first because it is
          the one that changes long conversations most, and because it costs
          nothing: no extra LLM call, and it works on platform keys too. */}
      {open && (<>
      <label className="flex items-start gap-3 mt-4 cursor-pointer select-none">
        <input
          data-testid="settings-conversation-aware-toggle"
          type="checkbox"
          checked={!!form.conversation_aware_enabled}
          onChange={(e) => setForm({ ...form, conversation_aware_enabled: e.target.checked })}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#1C1B1A] font-medium">
            Conversation-aware answers <span className="text-[10px] uppercase tracking-wider text-[#3A4A3E] ml-1">free · on by default</span>
          </div>
          <p className="text-xs text-[#6B6A68] leading-relaxed mt-0.5">
            When ON, a long conversation gets deeper instead of circling. Three things happen: a follow-up like "expand on that" is searched against the topic the visitor is actually drilling into rather than the literal words; your silo remembers which passages it has already quoted in this conversation and reaches for fresh material on later turns; and it is told to build on what it already said instead of restating it. A strong passage still wins even if it was already used — nothing is ever hidden from an answer, it only breaks ties in favour of material the visitor hasn't seen yet. Costs nothing extra per ask.
          </p>
        </div>
      </label>

      {/* Toggle 1 — Smart Question Rewriting (Item 17) */}
      <label className="flex items-start gap-3 mt-4 cursor-pointer select-none">
        <input
          data-testid="settings-query-expansion-toggle"
          type="checkbox"
          checked={!!form.query_expansion_enabled}
          onChange={(e) => setForm({ ...form, query_expansion_enabled: e.target.checked })}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#1C1B1A] font-medium">Smart Question Rewriting</div>
          <p className="text-xs text-[#6B6A68] leading-relaxed mt-0.5">
            Rewrites the visitor's question before your library is searched, then throws the
            rewrite away and answers the question they actually asked. It changes only which of
            your files get found — never how your answer sounds. That's Tone and Voice.
            Worth turning on when your files use different words than your fans do — scripture,
            medical, legal or academic material, where someone asks "the thing where bad has to
            exist for good to exist" and your library says "opposition in all things". Little to
            gain when your library is your own transcripts, since you already speak your fans'
            language. Needs your own Gemini key: on a platform key it stays off even when checked.
            Costs one extra short call per question — roughly 150–300 tokens on your key, pennies
            on Flash — and adds a moment of delay before the answer begins.
          </p>
        </div>
      </label>

      {/* Toggle 2 — Answer Evidence Check (Item 18) */}
      <label className="flex items-start gap-3 mt-4 cursor-pointer select-none">
        <input
          data-testid="settings-answer-verification-toggle"
          type="checkbox"
          checked={!!form.answer_verification_enabled}
          onChange={(e) => setForm({ ...form, answer_verification_enabled: e.target.checked })}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#1C1B1A] font-medium">Answer Evidence Check</div>
          <p className="text-xs text-[#6B6A68] leading-relaxed mt-0.5">
            When ON, every answer is checked against your library to make sure each claim is backed by real evidence — cuts hallucination on accuracy-critical silos. Adds a moderate extra cost per ask to YOUR BYOK provider key — the verification call is roughly the same size as the main answer call, so effectively DOUBLES your per-ask spend on this silo. Enable only when doctrinal/technical accuracy warrants the extra cost.
          </p>
        </div>
      </label>

      {/* Toggle 3 — Plain Human Voice (Sep 30, 2026, Randy pt-anti-ai).
          Free, default ON, applies in every voice mode. This is the slot he
          originally imagined "Double Down" in; it does that job with ~190
          tokens of rules instead of a third LLM call per ask. */}
      <label className="flex items-start gap-3 mt-4 cursor-pointer select-none">
        <input
          data-testid="settings-anti-ai-toggle"
          type="checkbox"
          checked={!!form.anti_ai_rules_enabled}
          onChange={(e) => setForm({ ...form, anti_ai_rules_enabled: e.target.checked })}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#1C1B1A] font-medium">
            Plain Human Voice <span className="text-[10px] uppercase tracking-[0.14em] text-[#2F6B3A]">free · on by default</span>
          </div>
          <p className="text-xs text-[#6B6A68] leading-relaxed mt-0.5">
            Stops answers reading like a machine wrote them. Three things give machine writing away,
            and this removes all three. First, long dashes, which look like this: “the body is the
            expression — the outside of infinite”. They are taken out of every answer after it is
            written, so that part is guaranteed. Second, sentences that all come out the same length,
            which is the loudest giveaway of the lot. Your silo is told to vary them instead. Third,
            stock machine phrasing: “Great question”, “not just X but Y”, repeating the question back
            at the reader, and a summary paragraph at the end that adds nothing. Costs{" "}
            <strong>nothing</strong> extra, because it adds rules to the request you are already
            paying for rather than making a second one.
          </p>
        </div>
      </label>

      {/* Jun 2026 (PIPELINE_SIMPLIFICATION_PROJECT.md item G) — the nudge.
          Relevant links default ON for a new YouTube Twin and OFF everywhere
          else, which is right on day one and wrong the day a creator finally
          uploads material full of links. Randy: "if they end up adding them
          they can opt and then return the setting on based on the alert."
          Shown once, only when acting on it would change something. */}
      <RelevantLinksNudge
        silo={silo}
        form={form}
        setForm={setForm}
        onSave={onSave}
        onUpdated={onUpdated}
      />

      {/* Toggle 4 — Relevant links (Jun 2026, Randy pt-relevant-links).
          Free, no extra LLM call. Default ON for a public silo built from
          YouTube videos, OFF for everything else — the server resolves that
          and hands back `relevant_links_effective`, so this checkbox shows
          what is actually happening instead of the frontend guessing. */}
      <label className="flex items-start gap-3 mt-4 cursor-pointer select-none">
        <input
          data-testid="settings-relevant-links-toggle"
          type="checkbox"
          checked={!!form.relevant_links_enabled}
          onChange={(e) => setForm({ ...form, relevant_links_enabled: e.target.checked })}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#1C1B1A] font-medium flex items-center gap-1.5 flex-wrap">
            <LinkSimple size={14} weight="duotone" className="text-[#3A4A3E]" />
            Relevant links
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#2F6B3A]">free</span>
          </div>
          <p className="text-xs text-[#6B6A68] leading-relaxed mt-0.5">
            A short closing offer underneath the answer — up to a couple of your own links a
            reader might want next. It does not touch the answer itself. Links that sit in the
            part of a file the answer came from already flow into the reply on their own; this
            is the bit that comes after, on its own line, the way you would tack "and here's the
            one I use" onto the end of a message. It reaches wider than the answer does on
            purpose: same arena as the question is enough, so a question about headlights can
            surface the switch you sell, a related build video, or your store. When nothing
            topical is left it will occasionally offer a channel-level link instead — your site,
            your merch — rather than nothing. In a conversation it keeps track: links already
            given step aside so the next answer reaches for something new, and it never prints a
            link the answer above already used. Only ever your own links, only ever from this
            silo.
          </p>
          <div className="mt-3 flex items-center gap-3 flex-wrap" onClick={(e) => e.preventDefault()}>
            <span className="text-xs text-[#1C1B1A]">Links in the closing offer</span>
            <input
              data-testid="settings-relevant-links-max"
              type="range"
              min={1}
              max={10}
              step={1}
              value={Number(form.relevant_links_max) || 2}
              disabled={!form.relevant_links_enabled || savingMax}
              onChange={(e) => setForm({ ...form, relevant_links_max: Number(e.target.value) })}
              onPointerUp={(e) => commitMax(Number(e.target.value))}
              onKeyUp={(e) => commitMax(Number(e.target.value))}
              className="w-40 accent-[#3A4A3E] disabled:opacity-40"
            />
            <span
              data-testid="settings-relevant-links-max-value"
              className="text-xs font-medium text-[#1C1B1A] tabular-nums w-16"
            >
              {Number(form.relevant_links_max) || 2} {(Number(form.relevant_links_max) || 2) === 1 ? "link" : "links"}
            </span>
          </div>
          <p className="text-[11px] text-[#6B6A68] leading-relaxed mt-1">
            A ceiling, never a quota. If only one link is worth offering, the closing line carries
            one and stops — it will never pad the offer to reach this number.
          </p>
        </div>
      </label>

      {/* Jun 2026 (PIPELINE_SIMPLIFICATION_PROJECT.md item F) — Randy's link
          library, directly under the slider he asked for it under. OUTSIDE the
          label on purpose: a text input inside a <label> hands its clicks to
          the label's checkbox, so searching the library would have silently
          switched Relevant links off. The drawer fetches nothing until opened. */}
      <LinkLibraryDrawer siloId={silo?.id} linkCount={silo?.link_count || 0} />

      {/* Oct 2026 (LINK_LIBRARY_AUTOFIX_PROJECT.md · Phase A3) — Randy's own
          sketch: one row directly under the library, two switches, tooltips.
          The `+` switch is a SUB-switch, not a sibling: tier 6 only ever runs on
          what the five free tiers could not name, so "AI on, free off" is not a
          state worth being able to reach. */}
      <div
        className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border hairline bg-[#FBFAF6] px-3 py-2"
        data-testid="settings-autofix-row"
      >
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="settings-autofix-links"
              checked={form.auto_fix_links !== false}
              onChange={(e) => setForm({
                ...form,
                auto_fix_links: e.target.checked,
                ...(e.target.checked ? {} : { auto_fix_links_model: false }),
              })}
              className="accent-[#3A4A3E]"
            />
            <span className="text-xs text-[#1C1B1A]">Auto fix (free)</span>
          </label>
          {/* OUTSIDE the label: InfoTip is a button, and a button inside a
              <label> hands its click to the checkbox — opening the tooltip
              would silently flip the setting. */}
          <InfoTip
            testid="settings-autofix-links-tip"
            text="When a new file lands, name its links from what you already have — a link to the same place you already named, the file's own title, the real YouTube title, the heading it sits under. Costs nothing, runs on its own, and only touches the new file's links. Hand edits are never changed."
          />
        </div>
        <div className="flex items-center gap-2">
          <label className={`flex items-center gap-2 ${form.auto_fix_links === false ? "opacity-40" : "cursor-pointer"}`}>
            <input
              type="checkbox"
              data-testid="settings-autofix-links-model"
              disabled={form.auto_fix_links === false}
              checked={form.auto_fix_links_model === true}
              onChange={(e) => setForm({ ...form, auto_fix_links_model: e.target.checked })}
              className="accent-[#3A4A3E]"
            />
            <span className="text-xs text-[#1C1B1A]">Auto fix + <span className="text-[#6B6A68]">(small LLM cost)</span></span>
          </label>
          <InfoTip
            testid="settings-autofix-links-model-tip"
            text="For the few links none of that explains, ask your model to name them. It only ever runs on what the free pass could not work out, uses your own key or this silo's voucher, and writes one short name per link — capped at 25 links per file."
          />
        </div>
        <span className="text-[11px] text-[#6B6A68] ml-auto">
          Runs on the new file&apos;s links only, just after it lands — and once more before a batch of replies is drafted.
        </span>
      </div>

      {/* Shared cost transparency note */}
      <p className="mt-4 text-[11px] text-[#6B6A68] leading-relaxed bg-[#F7F5EF] border border-[#E4E1D8] rounded-md px-3 py-2">
        Smart Question Rewriting and Answer Evidence Check run on YOUR silo's BYOK provider key — YOU pay, not the visitor. Enabling them roughly 1.5×–2× the per-ask token spend on YOUR Gemini/Anthropic/OpenAI bill. Currently active on the BYOK Gemini surface only; on non-Gemini keys those two toggles pass through unchanged until per-provider support ships. Conversation-aware answers, Plain Human Voice and Relevant links are the exceptions: none adds an LLM call or any measurable cost, and all three work on every provider including platform keys.
      </p>
      </>)}
    </section>
  );
}


// Jun 2026 (PIPELINE_SIMPLIFICATION_PROJECT.md item G) — a silo that has
// quietly accumulated links tells its owner once, rather than the platform
// flipping a setting on behind their back.
function RelevantLinksNudge({ silo, form, setForm, onSave, onUpdated }) {
  const [gone, setGone] = useState(false);
  if (gone || !silo?.relevant_links_suggest || form.relevant_links_enabled) return null;

  const n = silo.link_count || 0;
  const dismiss = async () => {
    setGone(true);
    try { await api.post(`/silos/${silo.id}/relevant-links-suggestion/dismiss`); } catch { /* cosmetic */ }
  };
  const turnOn = async () => {
    setGone(true);
    setForm({ ...form, relevant_links_enabled: true });
    try {
      await api.post(`/silos/${silo.id}/relevant-links-suggestion/dismiss`);
      await api.patch(`/silos/${silo.id}`, { relevant_links_enabled: true });
      toast.success("Relevant links are on.");
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not turn that on.");
    }
  };

  return (
    <div
      data-testid="relevant-links-nudge"
      className="mt-4 rounded-md border border-[#D8CFA8] bg-[#FBF6E4] px-3 py-2.5"
    >
      <div className="flex items-start gap-2.5">
        <LinkSimple size={15} weight="duotone" className="mt-0.5 shrink-0 text-[#7A5C3E]" />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-[#1C1B1A]">
            Your files now carry {n.toLocaleString()} link{n === 1 ? "" : "s"}
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-[#6B6A68]">
            Relevant links is off on this silo, so none of them are ever offered to a reader.
            Turning it on adds a short closing line with up to a couple of your own links. It
            costs nothing and does not touch the answer itself. We are asking rather than
            switching it on for you.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="relevant-links-nudge-enable"
              onClick={turnOn}
              className="rounded-md bg-[#3A4A3E] px-3 py-1.5 text-xs text-white hover:bg-[#2F3D32]"
            >Turn it on</button>
            <button
              type="button"
              data-testid="relevant-links-nudge-dismiss"
              onClick={dismiss}
              className="rounded-md border hairline bg-white px-3 py-1.5 text-xs text-[#6B6A68] hover:border-[#4A5D4E]"
            >No thanks</button>
          </div>
        </div>
      </div>
    </div>
  );
}


function SiloTypeCard({ silo, onUpdated }) {
  const [open, setOpen] = useState(false);
  const me = getUser();
  const isOwner = !!me && (me.id === silo?.owner_user_id || me.role === "super_admin");
  const kindKey = resolveSiloKind(silo);
  const meta = SILO_KIND_META[kindKey] || SILO_KIND_META.public;
  return (
    <section className="bg-[#FDFCF8] border hairline rounded-lg p-6 mb-6" data-testid="silo-type-card">
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-serif-display text-lg text-[#1C1B1A]">Silo type</h3>
            <SiloKindPill silo={silo} size="sm" />
            <InfoTip text="This decides the whole personality of the silo. Public silos show up on the marketplace with a Q&A page for fans. Personal silos are private (just you). Organizer silos add household + calendar + rewards + a family digest. You can convert between these anytime — nothing gets deleted." />
          </div>
          <p className="text-xs text-[#6B6A68] leading-relaxed max-w-2xl">{meta.blurb}</p>
        </div>
        {isOwner && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="silo-type-change-btn"
            className="text-xs px-4 py-2 rounded-md border-2 border-[#1C1B1A] bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] hover:border-[#3A4A3E] flex items-center gap-1.5 shrink-0"
          >
            <Sparkle size={12} weight="duotone" /> Change type
          </button>
        )}
      </div>
      {open && (
        <SiloKindConvertModal
          siloId={silo.id}
          currentKind={silo.silo_kind || "public"}
          currentYtTwin={!!silo.is_youtube_twin}
          onClose={() => setOpen(false)}
          onSaved={(updated) => { setOpen(false); onUpdated && onUpdated(updated); }}
        />
      )}
    </section>
  );
}

